/**
 * Phase 25 Plan 25-04 — useCreateComment.
 *
 * Phase 25 D-16 / D-18 — wait-for-server create.
 *
 * Flow:
 *   1) POST the comment — multipart with the staged files when there are any
 *      (MKS-188), plain JSON when there are none. The user-visible spinner
 *      waits on this round-trip (no optimistic prepend per D-16; the comment
 *      list relies on the server-issued id + canonical timestamps).
 *      Per-file failures are REPORTED by the server in `attachmentResults`,
 *      not thrown — the parent comment stays posted regardless because
 *      attachments are not bundled atomically (see 25-CONTEXT.md D-18).
 *   2) Invalidate the `['comments', taskId]` cache so the bubble + any
 *      attachment thumbnails reflect the server-canonical state.
 *
 * Phase 86 Plan 86-05 — a comment is what resets `lastActivityAt` (the whole
 * point of the My Tasks TOUCH chip: README "Interactions & behavior"). Every
 * caller of this hook — not just the TOUCH popover — now (a) invalidates the
 * my-tasks list on success, next to the existing board invalidation for the
 * identical reason, and (b) optimistically patches the touched task's
 * `lastActivityAt` in every cached my-tasks query BEFORE the round trip
 * resolves, so a row visibly leaves its stale/graveyard zone immediately
 * rather than waiting on the invalidated refetch. Snapshot-and-rollback
 * shape mirrors `useTaskMutation.ts` / `useUpdateTask.ts` (onMutate
 * snapshot, onError restore) rather than a bespoke one (T-86-20).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createComment,
  createCommentWithAttachments,
  type CommentDTO,
} from '../services/api/comments';
import { ApiClientError } from '../services/api/client';
import { MY_TASKS_BASE_KEY } from './useMyTasks';
import type { MyTaskDto } from '../services/api/tasks';

/**
 * Per-attachment outcome. The locked Phase 22 D-15 codes are surfaced verbatim
 * so the composer can render a precise inline error label per chip.
 */
export type AttachmentErrorCode =
  | 'ATTACHMENT_TOO_LARGE'
  | 'ATTACHMENT_DISALLOWED_TYPE'
  | 'ATTACHMENT_TOO_MANY'
  | 'UNKNOWN';

export interface AttachmentResult {
  filename: string;
  status: 'ok' | 'error';
  /** Locked Phase 22 D-15 code on error; `'UNKNOWN'` if the server returned no code. */
  code?: AttachmentErrorCode | string;
  message?: string;
}

/**
 * Quick task 260809-ofp (D-03) — the shared per-file error → AttachmentResult
 * extraction, factored out so `useCreatePortalRequest` (portal New-request
 * dropzone) reuses the SAME `ApiClientError.details.code` extraction instead
 * of a second implementation that could drift.
 */
export function attachmentResultFromError(filename: string, err: unknown): AttachmentResult {
  if (err instanceof ApiClientError) {
    const details = err.details as { code?: string } | undefined;
    return {
      filename,
      status: 'error',
      code: details?.code ?? 'UNKNOWN',
      message: err.message,
    };
  }
  return {
    filename,
    status: 'error',
    code: 'UNKNOWN',
    message: err instanceof Error ? err.message : 'Upload failed',
  };
}

export interface CreateCommentVariables {
  taskId: number;
  body: string;
  isInternalOnly: boolean;
  files: File[];
}

export interface CreateCommentResult {
  comment: CommentDTO;
  attachmentResults: AttachmentResult[];
}

interface CreateCommentContext {
  /** Every my-tasks cache entry (one per persona/filter suffix) as it stood
   * before the optimistic write — `getQueriesData` returns the full
   * `[queryKey, data]` pairs so `onError` can restore each one exactly. */
  previousMyTasksEntries: Array<[readonly unknown[], MyTaskDto[] | undefined]>;
}

export function useCreateComment() {
  const queryClient = useQueryClient();
  return useMutation<CreateCommentResult, Error, CreateCommentVariables, CreateCommentContext>({
    mutationFn: async ({ taskId, body, isInternalOnly, files }) => {
      // MKS-188 — one multipart request carries the comment AND its files, so
      // the server can persist them before it emails anyone. Only worth it when
      // something is actually attached; a plain comment keeps the JSON post.
      if (files.length > 0) {
        const { comment, attachmentResults } = await createCommentWithAttachments(
          taskId,
          { body, isInternalOnly },
          files,
        );
        return {
          comment,
          // Server-side shape is already this one; normalize only the code
          // fallback so the composer's per-chip label never reads `undefined`.
          attachmentResults: attachmentResults.map((r) =>
            r.status === 'ok' ? r : { ...r, code: r.code ?? 'UNKNOWN' },
          ),
        };
      }

      const comment = await createComment(taskId, { body, isInternalOnly });
      return { comment, attachmentResults: [] };
    },
    onMutate: async (vars) => {
      // Plan 86-05 (T-86-20) — snapshot every cached my-tasks query (the key
      // carries a persona/options suffix — MY_TASKS_BASE_KEY is a PREFIX
      // match) before touching any of them, so a rejected post can restore
      // each one exactly.
      const previousMyTasksEntries = queryClient.getQueriesData<MyTaskDto[]>({
        queryKey: MY_TASKS_BASE_KEY,
      });
      const touchedAt = new Date().toISOString();
      queryClient.setQueriesData<MyTaskDto[]>({ queryKey: MY_TASKS_BASE_KEY }, (rows) =>
        rows?.map((row) => (row.taskId === vars.taskId ? { ...row, lastActivityAt: touchedAt } : row)),
      );
      return { previousMyTasksEntries };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.previousMyTasksEntries) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['comments', vars.taskId] });
      // 260723 — a new comment counts as task activity, so refresh the board
      // caches too (all projectId/audience variants) → the staleness emoji
      // recomputes from the server's updated lastActivityAt. KanbanBoardPage
      // re-derives the open slide-over's task from board data, so the emoji
      // there updates without reopening.
      queryClient.invalidateQueries({ queryKey: ['board'] });
      // Plan 86-05 — same reasoning, for the My Tasks list: reconciles the
      // optimistic write above with the server-canonical lastActivityAt.
      queryClient.invalidateQueries({ queryKey: MY_TASKS_BASE_KEY });
      // 260806-ddg — commenting auto-subscribes the author (and any
      // mentioned users) as watchers; refresh the watchers panel.
      queryClient.invalidateQueries({ queryKey: ['tasks', vars.taskId, 'watchers'] });
    },
  });
}
